/* eslint-disable no-param-reassign */
/* eslint-disable react/sort-comp */
import React, { Component } from 'react';
import { WithTranslation, withTranslation } from '@michelin/central-provider';
import configs from '../../global-variables';

// Based on an example located: https://cuneyt.aliustaoglu.biz/en/using-google-maps-in-react-without-custom-libraries/

const DEF_LAT = 49.00710869685708;
const DEF_LNG = -101.1520675918579;

declare global {
  interface Window { google: any; }
}

let lastGoogleLang : string = '';

interface GMapsLatLon {
  lat: ()=>number,
  lng: ()=>number,
}

interface GMapsEvent {
  latLng: GMapsLatLon
}

interface GMaps {
  fitBounds: (bounds:any) => void
  setZoom: (zoomLevel:number) => void
  setCenter: (center:GMapsLatLon) => void
}

interface GMapsMarker {
  setPosition: (gmapsLatLon:GMapsLatLon) => void,
  position: GMapsLatLon,
  setMap: (gmaps:GMaps|null) => void,
  setDraggable: (draggable:boolean) => void,
  map: GMaps|null
}

interface MapProps extends WithTranslation {
  options:{
    center?: {
      lat: number,
      lng: number,
    },
    zoom: number,
  },
  label:string,
  id:string,
  onMapLoad?: (map:any)=>void,
  fixedMarker?: Marker,
  modifiedMarker?: Marker,
  readOnly?: boolean,
  onDragend?: (marker:Marker)=>void,
  style?: React.CSSProperties
}

interface Marker {
  lat:number,
  lng:number,
}

function sameMarker(a?:Marker, b?:Marker) {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return a.lat === b.lat && a.lng === b.lng;
}

function sameGMapsMarker(a?:GMapsMarker, b?:Marker) {
  if (!a && !b) return true;
  if (!a || !b) return false;
  if (!a.position) return false;
  return a.position.lat() === b.lat && a.position.lng() === b.lng;
}

class Map extends Component<MapProps, {}> {
  fixedMarker:GMapsMarker|null = null;
  modifiedMarker:GMapsMarker|null = null;
  map:GMaps|null = null;

  constructor(props:MapProps) {
    super(props);
    this.onScriptLoad = this.onScriptLoad.bind(this);
  }

  centerMap() {
    if (!this.fixedMarker || !this.map) return;
    const { google } = window;

    // create empty LatLngBounds object
    const bounds = new google.maps.LatLngBounds();

    // extend the bounds to include each marker's position
    if (this.modifiedMarker) bounds.extend(this.modifiedMarker.position);

    if (this.fixedMarker.map) {
      bounds.extend(this.fixedMarker.position);
      // now fit the map to the newly inclusive bounds
      this.map.fitBounds(bounds);
    } else if (this.modifiedMarker) {
      // now fit the map to the newly inclusive bounds
      this.map.setCenter(this.modifiedMarker.position);
    } else {
      this.map.setCenter(new window.google.maps.LatLng(DEF_LAT, DEF_LNG));
    }

    const listener = google.maps.event.addListener(this.map, 'idle', () => {
      google.maps.event.removeListener(listener);
    });
  }

  onScriptLoad() {
    const { t } = this.props;
    let { center } = this.props.options;
    let { zoom } = this.props.options;
    if (!center || (!center.lat && !center.lng)) {
      if (!this.props.fixedMarker || !this.props.fixedMarker.lat || !this.props.fixedMarker.lng) {
        center = { lat: DEF_LAT, lng: DEF_LNG };
        if (!this.props.modifiedMarker || (!this.props.modifiedMarker.lat && !this.props.modifiedMarker.lng)) {
          zoom = 2;
        }
      } else {
        center = {
          lat: this.props.fixedMarker.lat,
          lng: this.props.fixedMarker.lng,
        };
      }
    }

    this.map = new window.google.maps.Map(
      document.getElementById(this.props.id),
      { center, zoom },
    );
    if (this.props.onMapLoad) this.props.onMapLoad(this.map);
    this.modifiedMarker = new window.google.maps.Marker({
      icon: {
        url: '/static/images/red-dot.png',
        labelOrigin: { x: 16, y: 45 },
      },
      draggable: true,
      label: {
        text: t('Requested Position'),
        color: '#990000',
        align: 'right',
        fontWeight: 'bold',
      },
    });
    this.fixedMarker = new window.google.maps.Marker({
      icon: {
        url: '/static/images/green-dot.png',
        labelOrigin: { x: 16, y: 45 },
      },
      draggable: false,
      label: {
        text: this.props.label,
        color: '#005500',
        align: 'right',
        fontWeight: 'bold',
      },
    });
    this.setupDragEvents();
    this.renderMarker(this.props);
  }

  renderMarker(props:MapProps) {
    if (!this.fixedMarker || !this.modifiedMarker) return;
    if (!props.fixedMarker) {
      this.fixedMarker.setMap(null);
      return;
    }

    if (!props.fixedMarker.lat && !props.fixedMarker.lng) {
      this.fixedMarker.setMap(null);
    } else if (!this.fixedMarker.map) {
      this.fixedMarker.setMap(this.map);
    }
    if (!sameGMapsMarker(this.fixedMarker, props.fixedMarker)) {
      this.fixedMarker.setPosition(new window.google.maps.LatLng(
        props.fixedMarker.lat,
        props.fixedMarker.lng,
      ));
    }

    if (!props.modifiedMarker) {
      this.modifiedMarker.setMap(null);
      return;
    }
    if (props.readOnly && !props.modifiedMarker.lat) {
      this.modifiedMarker.setMap(null);
      return;
    }

    if (!props.readOnly && props.modifiedMarker.lat === 0 && props.modifiedMarker.lng === 0) {
      if (props.fixedMarker.lng || props.fixedMarker.lat) {
        props.modifiedMarker.lat = props.fixedMarker.lat;
        props.modifiedMarker.lng = props.fixedMarker.lng;
      } else {
        props.modifiedMarker.lat = DEF_LAT;
        props.modifiedMarker.lng = DEF_LNG;
      }
      props.modifiedMarker.lat -= 0.0003;
      props.modifiedMarker.lng += 0.0003;
      if (props.onDragend) props.onDragend(props.modifiedMarker);
    }

    if (!this.modifiedMarker.map) this.modifiedMarker.setMap(this.map);
    if (!sameGMapsMarker(this.modifiedMarker, props.modifiedMarker)) {
      this.modifiedMarker.setPosition(new window.google.maps.LatLng(
        props.modifiedMarker.lat,
        props.modifiedMarker.lng,
      ));
    }
    this.modifiedMarker.setDraggable(!props.readOnly);
    this.centerMap();
  }

  setupDragEvents() {
    window.google.maps.event.addListener(this.modifiedMarker, 'dragend', (event:GMapsEvent) => {
      if (!this.props.onDragend) return;
      this.props.onDragend({
        lat: event.latLng.lat(),
        lng: event.latLng.lng(),
      });
    });
  }

  shouldComponentUpdate(newProps:MapProps, newState:{}) {
    if (!sameMarker(newProps.fixedMarker, this.props.fixedMarker)) {
      this.renderMarker(newProps);
      if (!newProps.fixedMarker || !this.map) return true;
      this.map.setCenter(new window.google.maps.LatLng(newProps.fixedMarker));
      return true;
    }
    if (!sameMarker(newProps.modifiedMarker, this.props.modifiedMarker)) {
      this.renderMarker(newProps);
      return true;
    }
    if (this.props.readOnly !== newProps.readOnly) {
      this.renderMarker(newProps);
      return true;
    }
    return true;
  }

  componentDidMount() {
    const lang = this.props.i18n.language;
    if (!window.google || lang !== lastGoogleLang) {
      lastGoogleLang = lang;
      const s = document.createElement('script');
      s.type = 'text/javascript';
      s.src = `https://maps.google.com/maps/api/js?key=${configs.googleMapsApiKey}&language=${lang.replace('_', '-')}`;
      const x = document.getElementsByTagName('script')[0];
      if (!x || !x.parentNode) return;
      x.parentNode.insertBefore(s, x);
      s.addEventListener('load', (e) => {
        this.onScriptLoad();
      });
    } else {
      this.onScriptLoad();
    }
  }

  render() {
    return (
      <div style={{ width: '100%', height: 400, ...this.props.style }} id={this.props.id} />
    );
  }
}

const MapWithTranslation = withTranslation()(Map);

export {
  MapWithTranslation as Map,
};
